// src/pages/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Container,
    Flex,
    Heading,
    Text,
    useToast,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    useDisclosure,
    HStack,
    Image,
    Badge,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Spinner,
    Tooltip,
} from '@chakra-ui/react';
import {
    FiGrid,
    FiUser,
    FiShare2,
    FiLogOut,
    FiMenu,
    FiPlus,
    FiUpload,
    FiArrowLeft,
    FiBook,  // Adicione esta importação    
} from 'react-icons/fi';
import { supabase } from '../config/supabaseClient';
import logo from '../assets/logo.png';
import GalleryTab from '../components/dashboard/GalleryTab';
import ImageUploadTab from '../components/dashboard/ImageUploadTab';
import CouponTab from '../components/dashboard/CouponTab';
import ProfilePage from '../components/dashboard/ProfilePage';
import CreditsCheckoutAsaas from '../components/dashboard/CreditsCheckoutAsaas';
import ProfileModal from '../components/dashboard/ProfileModal';
import Footer from '../components/Footer';
import HeaderDashboard from '../components/dashboard/HeaderDashboard';

const Dashboard = () => {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [showProfile, setShowProfile] = useState(false);
    const [loadingImages, setLoadingImages] = useState(false);

    // Modais
    const {
        isOpen: isCheckoutOpen,
        onOpen: onCheckoutOpen,
        onClose: onCheckoutClose
    } = useDisclosure();

    const {
        isOpen: isProfileModalOpen,
        onOpen: onOpenProfileModal,
        onClose: onCloseProfileModal
    } = useDisclosure();

    const toast = useToast();
    const navigate = useNavigate();

    // No início do Dashboard.jsx, mantenha os imports e estados atuais

    useEffect(() => {
        const getUser = async () => {
            try {
                setLoading(true);

                // Usar getSession em vez de getUser
                const { data: { session }, error: sessionError } = await supabase.auth.getSession();

                if (sessionError) {
                    throw sessionError;
                }

                if (!session) {
                    // Se não houver sessão, redirecionar para login
                    navigate('/login');
                    return;
                }

                // Definir o usuário com base na sessão
                setUser(session.user);

                // Buscar perfil e imagens
                await fetchProfile(session.user.id);
                await fetchImages(session.user.id);

            } catch (error) {
                console.error("Erro ao verificar autenticação:", error);
                toast({
                    title: 'Erro de autenticação',
                    description: "Não foi possível verificar sua sessão. Tente fazer login novamente.",
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
                navigate('/login');
            } finally {
                setLoading(false);
            }
        };

        getUser();
    }, [navigate]);

    const handleViewProfile = () => {
        setShowProfile(true);
        setActiveTab(-1); // -1 indica que nenhuma aba está ativa
    };

    const handleBackFromProfile = () => {
        setShowProfile(false);
        setActiveTab(0); // Voltar para a primeira aba
    };

    const fetchProfile = async (userId) => {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

            if (error) {
                // Se o perfil não existe, criar um
                if (error.code === 'PGRST116') {
                    await createProfile(userId, user.email);
                    return;
                }
                return;
            }

            // Armazenar dados do perfil sem modificação
            setProfile(data);

            // Verificar se os campos obrigatórios estão preenchidos
            if (!data.cpf_cnpj || !data.postal_code || !data.address_number) {
                if (!isProfileModalOpen) {
                    onOpenProfileModal();
                }
            }
        } catch (err) {
            console.error('Erro inesperado ao buscar perfil:', err);
        }
    };


    // Função auxiliar para criar um perfil para o usuário atual
    const createProfile = async (userId, email) => {
        try {
            // Gerar um código de cupom aleatório
            const couponCode = `USR${Math.random().toString(36).substring(2, 7).toUpperCase()}`;

            // Obter dados do usuário para extrair nome e telefone
            const { data: userData } = await supabase.auth.getUser();

            // Determinar o nome a ser usado
            const name = userData?.user?.user_metadata?.full_name ||
                userData?.user?.user_metadata?.name ||
                email.split('@')[0];

            // Verificar se há telefone nos metadados
            const phone = userData?.user?.user_metadata?.phone || null;

            const { data, error } = await supabase
                .from('profiles')
                .insert([
                    {
                        id: userId,
                        name: name,
                        email: email,
                        phone: phone, // Usar telefone dos metadados se disponível
                        creditos: 0,
                        codigo_cupom: couponCode,
                        comissao_total: 0,
                        comissao_disponivel: 0,
                        cupom_used: false,
                        provider: userData?.user?.app_metadata?.provider || 'email'
                    }
                ])
                .select();

            if (error) {
                console.error('Erro ao criar perfil:', error);
                return;
            }

            setProfile(data[0]);
            // Abrir modal para completar perfil
            onOpenProfileModal();
        } catch (err) {
            console.error('Erro inesperado ao criar perfil:', err);
        }
    };

    const fetchImages = async (userId) => {
        try {
            setLoadingImages(true);

            // 1. Buscar imagens do banco de dados
            const { data, error } = await supabase
                .from('images')
                .select('*')
                .eq('user_id', userId)
                .order('created_at', { ascending: false });

            if (error) {
                console.error('Erro ao carregar imagens:', error);
                toast({
                    title: 'Erro ao carregar imagens',
                    description: error.message,
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                setLoadingImages(false);
                return;
            }

            // 2. Verificar e atualizar URLs públicas se necessário
            const processedImages = await Promise.all((data || []).map(async (img) => {
                // Verificar se as URLs são válidas
                let updatedImg = { ...img };

                // Se não tiver URL do original ou se a URL estiver desatualizada, gerar uma nova
                if (!img.original_image_url || img.original_image_url.includes('undefined')) {
                    // Extrair o caminho da URL atual, se existir
                    let path = '';
                    if (img.original_image_url) {
                        const parts = img.original_image_url.split('/');
                        // Pegar os últimos dois segmentos da URL (userId/fileName)
                        if (parts.length >= 2) {
                            path = `${parts[parts.length - 2]}/${parts[parts.length - 1]}`;
                        }
                    }

                    // Se não conseguimos extrair o caminho, usar um padrão baseado no ID da imagem
                    if (!path && img.id) {
                        path = `${userId}/original_${img.id}.png`;
                    }

                    // Gerar nova URL pública
                    const { data: newUrlData } = await supabase.storage
                        .from('original-images')
                        .getPublicUrl(path);

                    if (newUrlData && newUrlData.publicUrl) {
                        updatedImg.original_image_url = newUrlData.publicUrl;

                        // Atualizar no banco de dados
                        await supabase
                            .from('images')
                            .update({ original_image_url: newUrlData.publicUrl })
                            .eq('id', img.id);
                    }
                }

                // Repetir o processo para a imagem gerada
                if (!img.generated_image_url || img.generated_image_url.includes('undefined')) {
                    let path = '';
                    if (img.generated_image_url) {
                        const parts = img.generated_image_url.split('/');
                        if (parts.length >= 2) {
                            path = `${parts[parts.length - 2]}/${parts[parts.length - 1]}`;
                        }
                    }

                    if (!path && img.id) {
                        path = `${userId}/generated_${img.id}.png`;
                    }

                    const { data: newUrlData } = await supabase.storage
                        .from('generated-images')
                        .getPublicUrl(path);

                    if (newUrlData && newUrlData.publicUrl) {
                        updatedImg.generated_image_url = newUrlData.publicUrl;

                        // Atualizar no banco de dados
                        await supabase
                            .from('images')
                            .update({ generated_image_url: newUrlData.publicUrl })
                            .eq('id', img.id);
                    }
                }

                return updatedImg;
            }));

            setImages(processedImages);
            setLoadingImages(false);
        } catch (err) {
            console.error("Erro inesperado ao buscar imagens:", err);
            toast({
                title: 'Erro ao carregar imagens',
                description: 'Ocorreu um erro inesperado.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            setLoadingImages(false);
        }
    };

    const handleDeleteImage = async (imageId) => {
        try {
            // 1. Obter informações da imagem antes de excluir
            const { data: imageData, error: fetchError } = await supabase
                .from('images')
                .select('*')
                .eq('id', imageId)
                .single();

            if (fetchError) throw fetchError;

            // 2. Excluir o registro do banco de dados
            const { error: deleteError } = await supabase
                .from('images')
                .delete()
                .eq('id', imageId);

            if (deleteError) throw deleteError;

            // 3. Excluir arquivos do storage (opcional, dependendo da sua política de armazenamento)
            // Extrair nomes de arquivos das URLs
            const originalFilePath = imageData.original_image_url.split('/').pop();
            const generatedFilePath = imageData.generated_image_url.split('/').pop();

            if (originalFilePath) {
                await supabase.storage
                    .from('original-images')
                    .remove([`${user.id}/${originalFilePath}`]);
            }

            if (generatedFilePath) {
                await supabase.storage
                    .from('generated-images')
                    .remove([`${user.id}/${generatedFilePath}`]);
            }

            // 4. Atualizar a lista de imagens
            fetchImages(user.id);

            toast({
                title: 'Imagem excluída',
                description: 'A imagem foi removida com sucesso.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

        } catch (error) {
            console.error('Erro ao excluir imagem:', error);
            toast({
                title: 'Erro ao excluir',
                description: error.message || 'Ocorreu um erro ao excluir a imagem.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    // Função para atualizar URLs com URLs assinadas
    const refreshImageUrl = async (image) => {
        try {
            // Extrair o caminho do bucket e o caminho do arquivo da URL
            const originalUrl = image.original_image_url;
            const generatedUrl = image.generated_image_url;

            // Extrair o caminho do arquivo da URL
            const originalPath = originalUrl.split('/public/')[1].split('?')[0];
            const generatedPath = generatedUrl.split('/public/')[1].split('?')[0];

            // Criar novas URLs assinadas
            const { data: originalSignedData } = await supabase.storage
                .from('original-images')
                .createSignedUrl(originalPath, 60 * 60 * 24 * 7); // 7 dias

            const { data: generatedSignedData } = await supabase.storage
                .from('generated-images')
                .createSignedUrl(generatedPath, 60 * 60 * 24 * 7); // 7 dias

            // Atualizar o banco de dados
            await supabase
                .from('images')
                .update({
                    original_image_url: originalSignedData.signedUrl,
                    generated_image_url: generatedSignedData.signedUrl
                })
                .eq('id', image.id);

            // Retornar as novas URLs
            return {
                ...image,
                original_image_url: originalSignedData.signedUrl,
                generated_image_url: generatedSignedData.signedUrl
            };
        } catch (error) {
            console.error('Erro ao atualizar URLs da imagem:', error);
            return image; // Retorna a imagem original se houver erro
        }
    };

    const handleSignOut = async () => {
        const { error } = await supabase.auth.signOut();

        if (error) {
            toast({
                title: 'Erro ao sair',
                description: error.message,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        navigate('/login');
    };

    const handleProfileUpdate = () => {
        fetchProfile(user?.id);

        toast({
            title: 'Perfil atualizado',
            description: 'Suas informações foram atualizadas com sucesso.',
            status: 'success',
            duration: 3000,
            isClosable: true,
        });
    };

    const handleImagesUpdate = () => {
        fetchImages(user?.id);
    };

    const handleCheckoutOpen = () => {
        // Verificar se o usuário tem todos os dados obrigatórios cadastrados
        if (!profile?.cpf_cnpj || !profile?.postal_code || !profile?.address_number) {
            toast({
                title: 'Perfil incompleto',
                description: 'Para comprar créditos, é necessário completar seu perfil com todos os dados obrigatórios (CPF/CNPJ, CEP e número do endereço).',
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            onOpenProfileModal();
            return;
        }

        onCheckoutOpen();
    };

    if (loading) {
        return (
            <Flex height="100vh" align="center" justify="center">
                <Spinner size="xl" color="brand.primary" thickness="4px" />
            </Flex>
        );
    }

    return (
        <Box minH="100vh" bg="gray.50">
            {/* Header */}
            <HeaderDashboard
                profile={profile}
                user={user}
                showCredits={true}
                showBuyButton={true}
                onCheckoutOpen={handleCheckoutOpen}
                onSignOut={handleSignOut}
                onNavigate={(action) => {
                    if (typeof action === 'object' && action.tab !== undefined) {
                        setActiveTab(action.tab);
                        setShowProfile(false);
                    } else if (action === 'profile') {
                        handleViewProfile();
                    }
                }}
                currentPath="dashboard"
            />
            {/* Main Content */}
            <Container maxW="container.xl" py={8}>
                {/* Alerta se não tiver CPF/CNPJ ou endereço completo */}
                {(!profile?.cpf_cnpj || !profile?.postal_code || !profile?.address_number) && !showProfile && (
                    <Alert status="warning" mb={4}>
                        <AlertIcon />
                        <AlertTitle>Atenção!</AlertTitle>
                        <AlertDescription>
                            Para realizar pagamentos, é necessário completar seu perfil com todos os dados obrigatórios (CPF/CNPJ, CEP e número do endereço).
                            <Button
                                ml={2}
                                colorScheme="blue"
                                size="sm"
                                onClick={onOpenProfileModal}
                            >
                                Atualizar Perfil
                            </Button>
                        </AlertDescription>
                    </Alert>
                )}

                {showProfile ? (
                    <ProfilePage
                        userId={user?.id}
                        onProfileUpdate={handleProfileUpdate}
                        onBack={handleBackFromProfile}
                    />
                ) : (
                    <Tabs
                        variant="soft-rounded"
                        colorScheme="blue"
                        index={activeTab}
                        onChange={(index) => setActiveTab(index)}
                    >
                        <TabList mb={6}>
                            <Tab _selected={{ color: 'white', bg: 'brand.primary' }}>
                                <FiGrid style={{ marginRight: '8px' }} /> Galeria
                            </Tab>
                            <Tab _selected={{ color: 'white', bg: 'brand.primary' }}>
                                <FiUpload style={{ marginRight: '8px' }} /> Criar desenho
                            </Tab>
                            <Tab _selected={{ color: 'white', bg: 'brand.primary' }}>
                                <FiShare2 style={{ marginRight: '8px' }} /> Meu cupom
                            </Tab>
                        </TabList>

                        <TabPanels>
                            {/* Tab 1: Galeria de Imagens */}
                            <TabPanel>
                                {loadingImages ? (
                                    <Flex justify="center" align="center" py={10}>
                                        <Spinner size="xl" color="brand.primary" />
                                    </Flex>
                                ) : (
                                    <GalleryTab
                                        images={images}
                                        onCreateNew={() => setActiveTab(1)}
                                        onDelete={handleDeleteImage}
                                        refreshImageUrl={refreshImageUrl}
                                    />
                                )}
                            </TabPanel>

                            {/* Tab 2: Criar Desenho */}
                            <TabPanel>
                                <ImageUploadTab
                                    profile={profile}
                                    user={user}
                                    onCheckoutOpen={handleCheckoutOpen}
                                    onProfileUpdate={handleProfileUpdate}
                                    onImagesUpdate={handleImagesUpdate}
                                    onSuccess={() => setActiveTab(0)}
                                />
                            </TabPanel>

                            {/* Tab 3: Meu Cupom */}
                            <TabPanel>
                                <CouponTab profile={profile} />
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                )}
            </Container>

            {/* Botão flutuante para criar álbum */}
            <Box
                position="fixed"
                bottom={{ base: "24px", md: "30px" }}
                right={{ base: "24px", md: "30px" }}
                zIndex={10}
                display="flex"
                flexDirection="column"
                alignItems="flex-end"
                gap={4}
            >
                {/* Texto explicativo que aparece ao lado do botão em telas maiores */}
                <Flex
                    bg="purple.500"
                    color="white"
                    p={2}
                    borderRadius="md"
                    boxShadow="md"
                    mb={1}
                    display={{ base: "none", md: "flex" }}
                    alignItems="center"
                    opacity={0.9}
                    pr={3}
                    transition="all 0.3s"
                    _hover={{ opacity: 1 }}
                >
                    <Text fontWeight="medium">Criar Livro de Colorir</Text>
                </Flex>

                {/* Botão com animação de pulso suave */}
                <IconButton
                    icon={<FiBook size={26} />}
                    aria-label="Criar Livro de Colorir"
                    size="lg"
                    height="60px"
                    width="60px"
                    colorScheme="purple"
                    bgGradient="linear(to-r, purple.400, purple.600)"
                    isRound
                    boxShadow="xl"
                    onClick={() => navigate('/album-creator')}
                    _hover={{
                        transform: 'translateY(-5px)',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    }}
                    animation="pulse 2s infinite"
                    sx={{
                        '@keyframes pulse': {
                            '0%': { boxShadow: '0 0 0 0 rgba(159, 122, 234, 0.7)' },
                            '70%': { boxShadow: '0 0 0 15px rgba(159, 122, 234, 0)' },
                            '100%': { boxShadow: '0 0 0 0 rgba(159, 122, 234, 0)' },
                        }
                    }}
                />
            </Box>

            {/* Footer */}
            <Footer />

            {/* Modal de checkout de créditos */}
            <CreditsCheckoutAsaas
                isOpen={isCheckoutOpen}
                onClose={onCheckoutClose}
                userId={user?.id}
                onSuccess={handleProfileUpdate}
            />

            {/* Modal para atualização de perfil */}
            <ProfileModal
                isOpen={isProfileModalOpen}
                onClose={onCloseProfileModal}
                userId={user?.id}
                onProfileUpdate={handleProfileUpdate}
            />
        </Box>
    );
};

export default Dashboard;